import { useMemo, useState } from 'react';
import { type DealDetail, type DealScenario } from '@forecastify/shared';
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  Empty,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
  Tooltip,
  cn,
  type MenuItem,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useDuration } from '../../duration';
import { dealMoney, dealMoneyShort } from './deal-kit';
import { PlanConfirm } from './plan-kit';
import { NewScenarioDialog } from './scenario-dialogs';
import {
  pctText,
  scenarioKindLabel,
  scenarioStatusLabel,
  scenarioStatusVariant,
  spanText,
} from './scenario-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D9 · the deal's scenarios — `phase6/07`, `07d`.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Four tiles, the table, and the side-by-side comparison under it. Every figure is the server's
 * `figures` — computed by the same `scenarioFigures` the editor uses, options counted (DS-5).
 *
 * ⚠️ **The search filters in the browser.** The list endpoint answers a deal's every scenario in
 * one page — a deal carries a handful — so there is no cursor to page and no server query to send;
 * the filter narrows rows already fetched, and the empty state says how many it hid.
 *
 * ⚠️ **The comparison shows the ticked rows, or every row when fewer than two are ticked** — the
 * mockup compares all of them; ticking is how somebody narrows six down to the two that matter.
 */

export function ScenarioList({
  deal,
  scenarios,
  loading,
  writable,
  call,
  onOpen,
  onMarkWinner,
  onChanged,
  onDealChanged,
  toast,
}: {
  readonly deal: DealDetail;
  readonly scenarios: readonly DealScenario[];
  readonly loading: boolean;
  /** `deal.manage`, and the deal still open (DS-6). */
  readonly writable: boolean;
  readonly call: (rest: string, method?: string, body?: unknown) => Promise<Response | string>;
  readonly onOpen: (id: string) => void;
  readonly onMarkWinner: (scenario: { id: string; name: string }) => void;
  readonly onChanged: () => void;
  /** The deal itself changed (unmarking a winner answers with the deal). */
  readonly onDealChanged: () => void;
  readonly toast: (title: string, variant?: 'success' | 'error') => void;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const { locale } = useI18n();
  const [query, setQuery] = useState('');
  const [comparing, setComparing] = useState(false);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState<DealScenario | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const currency = deal.currencyCode;

  const shown = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase(locale);
    if (needle === '') return scenarios;
    return scenarios.filter((one) => one.name.toLocaleLowerCase(locale).includes(needle));
  }, [scenarios, query, locale]);

  const winner = scenarios.find((one) => one.isWinner) ?? null;
  const drafts = scenarios.filter((one) => one.status === 'draft').length;
  const bestRevenue = scenarios.reduce<DealScenario | null>(
    (best, one) =>
      best === null || Number(one.figures.revenue) > Number(best.figures.revenue) ? one : best,
    null,
  );
  const bestMargin = scenarios.reduce<DealScenario | null>(
    (best, one) =>
      one.figures.marginPct !== null &&
      (best?.figures.marginPct == null || one.figures.marginPct > best.figures.marginPct)
        ? one
        : best,
    null,
  );

  const tiles = [
    {
      key: 'count',
      label: t('dealScenario.tile.count'),
      value: String(scenarios.length),
      sub: t('dealScenario.tile.drafts', { count: drafts }),
      muted: false,
    },
    {
      key: 'winner',
      label: t('dealScenario.tile.winner'),
      value: winner?.name ?? '—',
      sub: winner === null ? t('dealScenario.tile.noWinner') : t('dealScenario.tile.winnerSub'),
      muted: winner === null,
    },
    {
      key: 'revenue',
      label: t('dealScenario.tile.bestRevenue'),
      value: bestRevenue === null ? '—' : dealMoneyShort(bestRevenue.figures.revenue, currency),
      sub: bestRevenue?.name ?? '',
      muted: false,
    },
    {
      key: 'margin',
      label: t('dealScenario.tile.bestMargin'),
      value: bestMargin === null ? '—' : pctText(bestMargin.figures.marginPct, locale),
      sub: bestMargin?.name ?? '',
      muted: false,
    },
  ];

  const duplicate = async (scenario: DealScenario): Promise<void> => {
    const answer = await call('', 'POST', {
      name: t('dealScenario.copyName', { name: scenario.name }).slice(0, 120),
      kind: scenario.kind,
      startsOn: scenario.startsOn,
      copyFromId: scenario.id,
    });
    if (typeof answer === 'string') {
      toast(answer, 'error');
      return;
    }
    toast(t('dealScenario.toast.duplicated'));
    onChanged();
  };

  const unmark = async (scenario: DealScenario): Promise<void> => {
    const answer = await call(`/${scenario.id}/winner`, 'DELETE');
    if (typeof answer === 'string') {
      toast(answer, 'error');
      return;
    }
    toast(t('dealScenario.toast.unmarked'));
    onChanged();
    onDealChanged();
  };

  const confirmDelete = async (): Promise<void> => {
    if (pending === null) return;
    const answer = await call(`/${pending.id}`, 'DELETE');
    if (typeof answer === 'string') {
      setPendingError(answer);
      return;
    }
    toast(t('dealScenario.toast.deleted', { name: pending.name }));
    setPending(null);
    onChanged();
  };

  const rowMenu = (scenario: DealScenario): MenuItem[] => [
    {
      label: t('dealScenario.action.open'),
      icon: 'arrow-right',
      onSelect: () => {
        onOpen(scenario.id);
      },
    },
    ...(writable
      ? [
          {
            label: t('dealScenario.action.duplicate'),
            icon: 'copy' as const,
            onSelect: () => void duplicate(scenario),
          },
          scenario.isWinner
            ? {
                label: t('dealScenario.action.unmark'),
                icon: 'x' as const,
                onSelect: () => void unmark(scenario),
              }
            : {
                label: t('dealScenario.action.markWinner'),
                icon: 'circle-check' as const,
                onSelect: () => {
                  onMarkWinner({ id: scenario.id, name: scenario.name });
                },
              },
          { type: 'separator' as const },
          {
            label: t('dealScenario.action.delete'),
            icon: 'trash' as const,
            destructive: true,
            onSelect: () => {
              setPendingError(null);
              setPending(scenario);
            },
          },
        ]
      : []),
  ];

  const compared =
    ticked.size >= 2 ? scenarios.filter((one) => ticked.has(one.id)) : [...scenarios];

  return (
    <div className="flex flex-col pb-10">
      <div className="grid grid-cols-2 border-b md:grid-cols-4">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className="border-r border-b px-4 py-2.5 last:border-r-0 md:border-b-0"
          >
            <div className="text-muted-foreground truncate text-[11.5px]">{tile.label}</div>
            <Tooltip asChild overflowOnly content={tile.value}>
              <div
                className={cn(
                  'mt-0.5 truncate text-[16px] font-medium whitespace-nowrap tabular-nums',
                  tile.muted && 'text-muted-foreground',
                )}
              >
                {tile.value}
              </div>
            </Tooltip>
            <div className="text-muted-foreground truncate text-[11px]">{tile.sub}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
        <div className="relative min-w-[220px] flex-1">
          <Icon
            name="search"
            size={14}
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
          />
          <TextInput
            value={query}
            className="pl-8"
            placeholder={t('dealScenario.search')}
            aria-label={t('dealScenario.search')}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          aria-pressed={comparing}
          disabled={scenarios.length === 0}
          onClick={() => {
            setComparing((on) => !on);
          }}
        >
          <Icon name="layout-grid" size={14} />
          {comparing ? t('dealScenario.compare.hide') : t('dealScenario.compare.show')}
        </Button>
        {writable ? (
          <Button
            size="sm"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Icon name="plus" size={14} />
            {t('dealScenario.action.new')}
          </Button>
        ) : null}
      </div>

      <Table label={t('dealScenario.list.label')}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-10 pl-4">
              <span className="sr-only">{t('dealScenario.compare.pick')}</span>
            </TableHead>
            <TableHead>{t('dealScenario.column.name')}</TableHead>
            <TableHead>{t('dealScenario.column.kind')}</TableHead>
            <TableHead>{t('dealScenario.column.status')}</TableHead>
            <TableHead className="text-right">{t('dealScenario.column.revenue')}</TableHead>
            <TableHead className="text-right">{t('dealScenario.column.cost')}</TableHead>
            <TableHead className="text-right">{t('dealScenario.column.margin')}</TableHead>
            <TableHead className="text-right">{t('dealScenario.column.hours')}</TableHead>
            <TableHead>{t('dealScenario.column.span')}</TableHead>
            <TableHead>{t('dealScenario.column.createdBy')}</TableHead>
            <TableHead className="w-10" aria-label={t('list.rowMenu')} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && scenarios.length === 0
            ? [0, 1, 2].map((row) => (
                <TableRow key={row} className="hover:bg-transparent">
                  <TableCell colSpan={11} className="pl-4">
                    <Skeleton width={row === 1 ? 70 : 90} />
                  </TableCell>
                </TableRow>
              ))
            : shown.map((scenario) => {
                const f = scenario.figures;
                const cost = Number(f.labour) + Number(f.external);
                const negative = Number(f.margin) < 0;
                return (
                  <TableRow
                    key={scenario.id}
                    className="cursor-pointer"
                    onClick={() => {
                      onOpen(scenario.id);
                    }}
                  >
                    <TableCell
                      className="pl-4"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <Checkbox
                        checked={ticked.has(scenario.id)}
                        label={t('dealScenario.compare.pickOne', { name: scenario.name })}
                        onCheckedChange={(next) => {
                          setTicked((current) => {
                            const copy = new Set(current);
                            if (next) copy.add(scenario.id);
                            else copy.delete(scenario.id);
                            return copy;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        {scenario.isWinner ? (
                          <Icon
                            name="circle-check"
                            size={14}
                            className="text-primary shrink-0"
                            aria-label={t('dealScenario.status.won')}
                          />
                        ) : (
                          <Icon
                            name="square-dashed"
                            size={14}
                            className="text-muted-foreground shrink-0"
                          />
                        )}
                        <span className="font-medium">{scenario.name}</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {scenarioKindLabel(t, scenario.kind)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={scenarioStatusVariant(scenario.status)}>
                        {scenarioStatusLabel(t, scenario.status)}
                      </Badge>
                    </TableCell>
                    <TableCell numeric>{dealMoney(f.revenue, currency)}</TableCell>
                    <TableCell numeric>{dealMoney(cost, currency)}</TableCell>
                    <TableCell numeric className={cn(negative && 'text-destructive')}>
                      {`${dealMoney(f.margin, currency)} · ${pctText(f.marginPct, locale)}`}
                    </TableCell>
                    <TableCell numeric>{fmt(f.hours)}</TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {spanText(f.from, f.to, locale)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {scenario.createdByName ?? '—'}
                    </TableCell>
                    <TableCell
                      className="pr-3"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <DropdownMenu label={t('list.rowMenu')} align="end" items={rowMenu(scenario)}>
                        <Button variant="ghost" size="icon-sm" aria-label={t('list.rowMenu')}>
                          <Icon name="ellipsis" size={16} />
                        </Button>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>

      {!loading && scenarios.length === 0 ? (
        <Empty
          icon="square-dashed"
          title={t('dealScenario.empty.title')}
          body={t('dealScenario.empty.body')}
          {...(writable
            ? {
                actionLabel: t('dealScenario.action.new'),
                onAction: () => {
                  setCreating(true);
                },
              }
            : {})}
        />
      ) : null}
      {scenarios.length > 0 && shown.length === 0 ? (
        <Empty
          icon="search"
          title={t('dealScenario.empty.filtered')}
          body={t('dealScenario.empty.filteredBody', { count: scenarios.length })}
          actionLabel={t('dealScenario.empty.clear')}
          onAction={() => {
            setQuery('');
          }}
        />
      ) : null}

      <div className="text-muted-foreground border-b bg-[color-mix(in_oklch,var(--muted)_40%,var(--background))] px-4 py-2.5 text-[12px] tabular-nums">
        {`${deal.identifier} · ${t('dealScenario.footer.count', { count: scenarios.length })} · ${
          winner === null
            ? t('dealScenario.footer.noWinner')
            : t('dealScenario.footer.winner', { name: winner.name })
        }`}
      </div>

      {comparing && compared.length > 0 ? (
        <ScenarioCompare
          scenarios={compared}
          currency={currency}
          onHide={() => {
            setComparing(false);
          }}
        />
      ) : null}

      <NewScenarioDialog
        open={creating}
        call={call}
        onClose={() => {
          setCreating(false);
        }}
        onCreated={(id) => {
          toast(t('dealScenario.toast.created'));
          onOpen(id);
        }}
      />
      <PlanConfirm
        open={pending !== null}
        title={t('dealScenario.confirm.deleteTitle')}
        body={t('dealScenario.confirm.deleteBody', { name: pending?.name ?? '' })}
        confirmLabel={t('common.delete')}
        error={pendingError}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}

/**
 * `07d` · the metrics side by side, a column per scenario, the best value in each row marked —
 * in the primary ink AND the medium weight, never colour alone (`state-rules.md` §10).
 */
function ScenarioCompare({
  scenarios,
  currency,
  onHide,
}: {
  readonly scenarios: readonly DealScenario[];
  readonly currency: string;
  readonly onHide: () => void;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const { locale } = useI18n();
  const figures = scenarios.map((one) => {
    const f = one.figures;
    const revenue = Number(f.revenue);
    const hours = Number(f.hours);
    return {
      revenue,
      labour: Number(f.labour),
      external: Number(f.external),
      margin: Number(f.margin),
      marginPct: f.marginPct,
      hours,
      perHour: hours > 0 ? revenue / hours : null,
      span: spanText(f.from, f.to, locale),
    };
  });
  type Figure = (typeof figures)[number];
  const money = (value: number | null): string =>
    value === null ? '—' : dealMoney(Math.round(value), currency);

  const rows: {
    key: string;
    label: string;
    get: (f: Figure) => number | null;
    format: (value: number | null) => string;
    best: 'high' | 'low' | 'none';
  }[] = [
    {
      key: 'revenue',
      label: t('dealScenario.tile.revenue'),
      get: (f) => f.revenue,
      format: money,
      best: 'high',
    },
    {
      key: 'labour',
      label: t('dealScenario.tile.labour'),
      get: (f) => f.labour,
      format: money,
      best: 'low',
    },
    {
      key: 'external',
      label: t('dealScenario.tile.external'),
      get: (f) => f.external,
      format: money,
      best: 'low',
    },
    {
      key: 'margin',
      label: t('dealScenario.tile.margin'),
      get: (f) => f.margin,
      format: money,
      best: 'high',
    },
    {
      key: 'marginPct',
      label: t('dealScenario.compare.marginPct'),
      get: (f) => f.marginPct,
      format: (value) => pctText(value, locale),
      best: 'high',
    },
    {
      key: 'hours',
      label: t('dealScenario.tile.hours'),
      get: (f) => f.hours,
      format: (value) => (value === null ? '—' : fmt(value)),
      best: 'none',
    },
    {
      key: 'perHour',
      label: t('dealScenario.tile.perHour'),
      get: (f) => f.perHour,
      format: money,
      best: 'high',
    },
  ];

  return (
    <section className="min-w-0">
      <div className="flex items-center gap-2 px-4 pt-5 pb-2">
        <h2 className="text-[13px] font-medium">{t('dealScenario.compare.title')}</h2>
        <span className="text-muted-foreground text-[12px]">
          {t('dealScenario.compare.hint', { count: scenarios.length })}
        </span>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onHide}>
          {t('dealScenario.compare.close')}
        </Button>
      </div>
      <Table label={t('dealScenario.compare.title')} className="w-max min-w-full">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-[220px] pl-4">{t('dealScenario.compare.metric')}</TableHead>
            {scenarios.map((one) => (
              <TableHead key={one.id} className="h-auto w-[180px] py-1.5 text-right normal-case">
                <div className="text-foreground truncate text-[12px] tracking-normal">
                  {one.name}
                </div>
                <div className="truncate text-[11px] tracking-normal">
                  {one.isWinner
                    ? `${scenarioKindLabel(t, one.kind)} · ${t('dealScenario.status.won')}`
                    : scenarioKindLabel(t, one.kind)}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const values = figures.map(row.get);
            const known = values.filter((value): value is number => value !== null);
            const best =
              row.best === 'none' || known.length < 2
                ? null
                : row.best === 'high'
                  ? Math.max(...known)
                  : Math.min(...known);
            return (
              <TableRow key={row.key}>
                <TableCell className="pl-4">{row.label}</TableCell>
                {values.map((value, index) => (
                  <TableCell
                    key={scenarios[index]?.id ?? index}
                    numeric
                    className={cn(best !== null && value === best && 'text-primary font-medium')}
                  >
                    {row.format(value)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell className="pl-4">{t('dealScenario.tile.span')}</TableCell>
            {figures.map((f, index) => (
              <TableCell key={scenarios[index]?.id ?? index} numeric>
                {f.span}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </section>
  );
}
